/**
 * resolveEffectiveLogo — the SINGLE source of truth for the "effective"
 * brand logo shown across the app (the top-menu-center wordmark AND the
 * report cover/template default).
 *
 * Per-org / per-user LOGO branding (feat/org-logo):
 *   1. A user can upload a PERSONAL logo from the user menu. That logo
 *      overrides everything for that user.
 *   2. A company admin can set an ORG logo (admin → Users & Orgs). That
 *      logo is the org-wide brand for every member.
 *   3. Otherwise the platform-default Datacrate wordmark is used.
 *
 * PRECEDENCE (effective logo):
 *   user-uploaded logo  →  org logo  →  Datacrate default
 *
 * Both the top-bar logo and `resolveReportBranding`'s `logoUrl` source
 * call THIS helper so they can never diverge. The report cover is left
 * untouched: it still resolves
 *   `branding.primaryLogoUrl ?? theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL`
 * and `resolveReportBranding` feeds the effective logo into that chain.
 */

/** Platform-default brand logo. Mirrors `DEFAULT_PRIMARY_COVER_LOGO_URL`
 *  in `reportBranding.ts` (kept independent so this helper has no import
 *  cycle with the report-cover branding module). */
export const DEFAULT_BRAND_LOGO_URL = '/datacrate-logo.svg';

export interface EffectiveLogoInput {
  /** The signed-in user's personal logo (users/{uid}/profile/base.logoUrl). */
  userLogoUrl?: string | null;
  /** The user's org logo (companies/{cid}/settings/branding.logoUrl). */
  orgLogoUrl?: string | null;
  /**
   * Optional override for the platform default — e.g. a host that white-labels
   * the fallback. Defaults to the Datacrate wordmark.
   */
  defaultLogoUrl?: string;
}

/** First non-empty (trimmed) string, else undefined. Mirrors the
 *  `firstMeaningful` guard used in `reportBranding.ts` so empty strings
 *  and whitespace-only values never count as a "set" logo. */
function firstMeaningful(...values: Array<string | null | undefined>): string | undefined {
  for (const v of values) {
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return undefined;
}

/**
 * Resolve the effective logo URL for the current user, applying the
 * user → org → default precedence. ALWAYS returns a non-empty URL.
 */
export function resolveEffectiveLogo(input: EffectiveLogoInput): string {
  const fallback = firstMeaningful(input.defaultLogoUrl) ?? DEFAULT_BRAND_LOGO_URL;
  return firstMeaningful(input.userLogoUrl, input.orgLogoUrl) ?? fallback;
}

/**
 * Resolve the effective UPLOADED logo (user → org), or `null` when neither
 * is set. Unlike `resolveEffectiveLogo` this does NOT substitute the
 * platform default — callers that want the default to flow through a
 * separate fallback chain (e.g. the report cover's
 * `template → theme → effective → default`) use this so the default is
 * only applied once, at the end of that chain.
 */
export function resolveUploadedLogo(input: EffectiveLogoInput): string | null {
  return firstMeaningful(input.userLogoUrl, input.orgLogoUrl) ?? null;
}
